import React from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@common/components/modal';
import { Button } from '@common/components/button';
import { Checkbox } from '@common/components/form/inputs/checkbox/checkbox-input';
import { TextInput } from '@common/components/form/inputs/text/text-input';
import Icon from '@common/components/icon';

export const MAX_LOCATIONS = 10;

export type EditableLocation = {
  id: string;
  place_id: string | null;
  name: string;
  city: string | null;
  is_head_office: boolean;
  selected: boolean;
};

type Props = {
  open: boolean;
  locations: EditableLocation[];
  onChange: (locations: EditableLocation[]) => void;
  onClose: () => void;
};

/** Untick, rename, pick the head office or add a location the research missed. */
export const LocationsEditor = ({
  open, locations, onChange, onClose,
}: Props) => {
  const { t } = useTranslation();
  const [newName, setNewName] = React.useState('');
  const selectedCount = locations.filter((location) => location.selected).length;

  const update = (id: string, patch: Partial<EditableLocation>) => onChange(
    locations.map((location) => (location.id === id ? { ...location, ...patch } : location)),
  );

  const setHeadOffice = (id: string) => onChange(
    locations.map((location) => ({
      ...location,
      is_head_office: location.id === id,
      selected: location.id === id ? true : location.selected,
    })),
  );

  const add = () => {
    const name = newName.trim();

    if (!name || selectedCount >= MAX_LOCATIONS) return;

    onChange([...locations, {
      id: `new-${Date.now()}`,
      place_id: null,
      name,
      city: null,
      is_head_office: false,
      selected: true,
    }]);
    setNewName('');
  };

  return (
    <Modal
      open={open}
      onOpenChange={(isOpen) => !isOpen && onClose()}
      size="sm"
      title={t('authentication:trial_preview_locations_edit_title')}
      description={t('authentication:trial_preview_locations_edit_hint')}
      confirmButtonText={t('authentication:trial_preview_locations_done')}
      onConfirm={onClose}
      hideFooter={false}
      content={(
        <div className="FreeTrial__LocationsEditor">
          {locations.map((location) => (
            <div key={location.id} className="FreeTrial__LocationsEditor__Row">
              <Checkbox
                value={location.selected}
                onChange={(selected) => update(location.id, { selected })}
                disabled={!location.selected && selectedCount >= MAX_LOCATIONS}
              />
              <TextInput
                value={location.name}
                onChange={(e) => update(location.id, { name: e.target.value })}
                aria-label={t('authentication:trial_preview_location_name')}
              />
              <label className="FreeTrial__LocationsEditor__HeadOffice">
                <input
                  type="radio"
                  name="head-office"
                  checked={location.is_head_office}
                  onChange={() => setHeadOffice(location.id)}
                />
                {t('authentication:trial_preview_head_office')}
              </label>
            </div>
          ))}
          <div className="FreeTrial__LocationsEditor__Add">
            <Icon type="place" size="small" />
            <TextInput
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
              placeholder={t('authentication:trial_preview_location_name')}
              disabled={selectedCount >= MAX_LOCATIONS}
            />
            <Button type="inverted" onClick={add} disabled={!newName.trim() || selectedCount >= MAX_LOCATIONS}>
              {t('authentication:trial_preview_add_location')}
            </Button>
          </div>
        </div>
      )}
    />
  );
};
